import { CheckCircleIcon, LockClosedIcon } from "@heroicons/react/20/solid";
import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { mainPopUpPortalId } from "~/constants/portalIds.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import StaffingClosingStatusesHoverCard
	from "~/modules/billing/components/PendingInvoicesIndexView/components/StaffingClosingStatusesHoverCard.tsx";
import type {
	PendingInvoicesIndexTableData,
	UnprocessedInvoicesIndexTableMeta,
} from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { projectNumberSortingFunction } from "~/modules/billing/utils/billingUtils.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { customArrayIncludesSome } from "~/utils/tanStackTableUtils.ts";

const columnHelper = createColumnHelper<PendingInvoicesIndexTableData>();

export const pendingInvoicesIndexTableColumnDefs = [
	columnHelper.accessor("allStaffingsClosed", {
		header: () => <LockClosedIcon className="size-5 fill-primary-500" />,
		meta: {
			cellTextAlign: "center",
			width: "4rem",
		},
		cell: (props) => {
			const allStaffingsClosed = props.getValue();
			return <div className="flex justify-center">
				<StaffingClosingStatusesHoverCard portalId={mainPopUpPortalId} closingStatuses={props.row.original.staffingsClosingStatuses}>
				<CheckCircleIcon className={clsx("size-8", allStaffingsClosed ? "fill-success-500" : "fill-gray-300")} />
				</StaffingClosingStatusesHoverCard>
			</div>;
		},
		filterFn: customArrayIncludesSome,
	}),
	columnHelper.accessor("companyDisplayName", {
		header: "Kunde",
		meta: {
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const companyName = props.getValue();
			const rowData = props.row.original;
			return <div className="flex items-center gap-x-2">
				{rowData.companyAvatarImage && <CompanyLogo image={rowData.companyAvatarImage}
															sizeClassName="size-6" />}
				<div className="truncate">{companyName}</div>
			</div>;
		},
		filterFn:(row, _, value) => {
			if (!Array.isArray(value)) {
				return false;
			}

			if (value.length === 0) {
				return true;
			}
			return value.includes(row.original.companyId);
		},
	}),
	columnHelper.accessor("projectNumberWithPhaseNumber", {
		header: "Projekt-ID",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "10rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		sortingFn: projectNumberSortingFunction,
	}),
	columnHelper.accessor("projectTitle", {
		header: "Titel",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "14rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
	}),
	columnHelper.accessor("subtotalCents", {
		header: "Rechnungsbetrag (netto)",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "right",
			width: "10rem",
		},
		cell: (props) => {
			return <div className="font-bold">{formatNumberToCurrency(props.getValue())} €</div>;
		},
		footer: (props) => {
			const total = props.table.getFilteredRowModel().rows.reduce((sum, row) => {
				return sum + (row.original.subtotalCents || 0);
			}, 0);
			return <div className="flex w-full items-center justify-between text-sm font-bold">
				<span>&sum;</span><span>{formatNumberToCurrency(total)} €</span></div>;
		},
	}),
	columnHelper.accessor("deliveryStartDate", {
		header: "Leistungszeitraum",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const rowData = props.row.original;
			if (!rowData.deliveryStartDate || !rowData.deliveryEndDate) return EMPTY_VALUE_PLACEHOLDER;
			return formatSmartDateRange(rowData.deliveryStartDate, rowData.deliveryEndDate, {
				month: "MMM",
				year: " yy",
			});
		},
	}),
	columnHelper.accessor("comment", {
		header: "Kommentar",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
		},
		cell: (props) => {
			const comment = props.getValue();
			const { onUpdateCommentClick } = props.table.options.meta as UnprocessedInvoicesIndexTableMeta;

			const Button = <button onClick={(event) => onUpdateCommentClick(event, props.row)}
								   className="flex w-full items-center justify-center gap-x-2 rounded-lg px-2 py-1 text-left hover:bg-yellow-200">
				<div className="truncate font-bold">{comment || EMPTY_VALUE_PLACEHOLDER}</div>
			</button>;

			if (!comment) {
				return Button;
			}

			return <Tooltip text={comment}
							className="max-w-96 whitespace-pre-wrap break-words text-left">
				{Button}
			</Tooltip>;
		},
		filterFn: customArrayIncludesSome,
	}),
];